import api from './api';

export interface ChartOfAccount {
  id: number;
  code: string;
  name: string;
  account_type: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'REVENUE' | 'EXPENSE' | 'CONTRA_ASSET' | 'CONTRA_LIABILITY' | 'CONTRA_EQUITY' | 'CONTRA_REVENUE';
  normal_balance: 'DEBIT' | 'CREDIT';
  parent_id?: number;
  property_id?: number;
  description?: string;
  is_active: boolean;
  is_system_account: boolean;
  full_code: string;
  full_name: string;
  parent?: ChartOfAccount;
  children?: ChartOfAccount[];
  created_at: string;
  updated_at: string;
}

export interface AccountingTransaction {
  id: number;
  transaction_date: string;
  reference_number: string;
  description: string;
  transaction_type: 'BOOKING_REVENUE' | 'EXPENSE' | 'PAYMENT_RECEIVED' | 'PAYMENT_MADE' | 'ADJUSTMENT' | 'DEPRECIATION' | 'ACCRUAL' | 'REVERSAL';
  total_amount: number;
  currency_id: number;
  property_id: number;
  unit_id?: number;
  unit_number?: string;
  unit_name?: string;
  created_by: number;
  status: 'DRAFT' | 'POSTED' | 'REVERSED' | 'CANCELLED';
  is_automated: boolean;
  source_type?: string;
  source_id?: number;
  currency?: any;
  property?: any;
  unit?: any;
  creator?: any;
  journal_entries?: JournalEntry[];
  created_at: string;
  updated_at: string;
}

export interface JournalEntry {
  id: number;
  account_id: number;
  transaction_id: number;
  debit_amount: number;
  credit_amount: number;
  description: string;
  reference_type?: string;
  reference_id?: number;
  property_id: number;
  account?: {
    id: number;
    name: string;
    code: string;
    account_type: string;
  };
  created_at: string;
  updated_at: string;
}

export interface AccountingDashboard {
  total_revenue: number;
  total_expenses: number;
  net_income: number;
  accounts_receivable: number;
  accounts_payable: number;
  cash_balance: number;
  recent_transactions: AccountingTransaction[];
  monthly_revenue: Array<{ year: number; month: number; total: number }>;
  monthly_expenses: Array<{ year: number; month: number; total: number }>;
}

export interface CreateAccountRequest {
  code: string;
  name: string;
  account_type: string;
  normal_balance: string;
  property_id?: number;
  parent_id?: number;
  description?: string;
}

export interface CreateTransactionRequest {
  transaction_date: string;
  description: string;
  transaction_type: string;
  property_id: number;
  unit_id?: number;
  unit_number?: string;
  unit_name?: string;
  currency_id: number;
  journal_entries: Array<{
    account_id: number;
    debit_amount?: number;
    credit_amount?: number;
    description: string;
  }>;
  auto_post?: boolean;
}

export interface ReportRequest {
  report_type: 'balance_sheet' | 'income_statement' | 'trial_balance' | 'general_ledger';
  property_id?: number;
  unit_id?: number;
  start_date: string;
  end_date: string;
}

// Mock data for Chart of Accounts
const mockChartOfAccounts: ChartOfAccount[] = [
  {
    id: 1,
    code: '1000',
    name: 'Cash and Cash Equivalents',
    account_type: 'ASSET',
    normal_balance: 'DEBIT',
    property_id: 1,
    description: 'Main operating cash account',
    is_active: true,
    is_system_account: true,
    full_code: '1000',
    full_name: 'Cash and Cash Equivalents',
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
    children: [
      {
        id: 11,
        code: '1001',
        name: 'Checking Account',
        account_type: 'ASSET',
        normal_balance: 'DEBIT',
        parent_id: 1,
        property_id: 1,
        is_active: true,
        is_system_account: false,
        full_code: '1000-1001',
        full_name: 'Cash and Cash Equivalents - Checking Account',
        created_at: '2024-01-01T00:00:00Z',
        updated_at: '2024-01-01T00:00:00Z'
      },
      {
        id: 12,
        code: '1002',
        name: 'Savings Account',
        account_type: 'ASSET',
        normal_balance: 'DEBIT',
        parent_id: 1,
        property_id: 1,
        is_active: true,
        is_system_account: false,
        full_code: '1000-1002',
        full_name: 'Cash and Cash Equivalents - Savings Account',
        created_at: '2024-01-01T00:00:00Z',
        updated_at: '2024-01-01T00:00:00Z'
      }
    ]
  },
  {
    id: 2,
    code: '1100',
    name: 'Accounts Receivable',
    account_type: 'ASSET',
    normal_balance: 'DEBIT',
    property_id: 1,
    description: 'Money owed by tenants',
    is_active: true,
    is_system_account: true,
    full_code: '1100',
    full_name: 'Accounts Receivable',
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  },
  {
    id: 3,
    code: '1200',
    name: 'Property and Equipment',
    account_type: 'ASSET',
    normal_balance: 'DEBIT',
    property_id: 1,
    description: 'Fixed assets including buildings and equipment',
    is_active: true,
    is_system_account: true,
    full_code: '1200',
    full_name: 'Property and Equipment',
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  },
  {
    id: 4,
    code: '2000',
    name: 'Accounts Payable',
    account_type: 'LIABILITY',
    normal_balance: 'CREDIT',
    property_id: 1,
    description: 'Money owed to suppliers and vendors',
    is_active: true,
    is_system_account: true,
    full_code: '2000',
    full_name: 'Accounts Payable',
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  },
  {
    id: 5,
    code: '2100',
    name: 'Security Deposits',
    account_type: 'LIABILITY',
    normal_balance: 'CREDIT',
    property_id: 1,
    description: 'Tenant security deposits held',
    is_active: true,
    is_system_account: false,
    full_code: '2100',
    full_name: 'Security Deposits',
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  },
  {
    id: 6,
    code: '3000',
    name: 'Owner Equity',
    account_type: 'EQUITY',
    normal_balance: 'CREDIT',
    property_id: 1,
    description: 'Owner investment and retained earnings',
    is_active: true,
    is_system_account: true,
    full_code: '3000',
    full_name: 'Owner Equity',
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  },
  {
    id: 7,
    code: '4000',
    name: 'Rental Income',
    account_type: 'REVENUE',
    normal_balance: 'CREDIT',
    property_id: 1,
    description: 'Income from rental properties',
    is_active: true,
    is_system_account: true,
    full_code: '4000',
    full_name: 'Rental Income',
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  },
  {
    id: 8,
    code: '5000',
    name: 'Property Maintenance',
    account_type: 'EXPENSE',
    normal_balance: 'DEBIT',
    property_id: 1,
    description: 'Costs for property upkeep and repairs',
    is_active: true,
    is_system_account: false,
    full_code: '5000',
    full_name: 'Property Maintenance',
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  },
  {
    id: 9,
    code: '5100',
    name: 'Utilities',
    account_type: 'EXPENSE',
    normal_balance: 'DEBIT',
    property_id: 1,
    description: 'Water, electricity, gas, and other utilities',
    is_active: true,
    is_system_account: false,
    full_code: '5100',
    full_name: 'Utilities',
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  },
  {
    id: 10,
    code: '5200',
    name: 'Property Management Fees',
    account_type: 'EXPENSE',
    normal_balance: 'DEBIT',
    property_id: 1,
    description: 'Fees paid to property management companies',
    is_active: true,
    is_system_account: false,
    full_code: '5200',
    full_name: 'Property Management Fees',
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  }
];

export class AccountingService {
  static async getDashboard(propertyId?: number): Promise<AccountingDashboard> {
    const params = propertyId ? { property_id: propertyId } : {};
    const response = await api.get('/accounting/dashboard', { params });
    return response.data.data;
  }

  static async getChartOfAccounts(propertyId?: number): Promise<ChartOfAccount[]> {
    console.log('AccountingService.getChartOfAccounts called with propertyId:', propertyId);
    
    const params = propertyId ? { property_id: propertyId } : {};
    console.log('API request params:', params);
    
    try {
      const response = await api.get('/accounting/chart-of-accounts', { params });
      console.log('AccountingService.getChartOfAccounts response:', response);
      console.log('Response data:', response.data);
      
      return response.data.data || [];
    } catch (error) {
      console.error('AccountingService.getChartOfAccounts error:', error);
      throw error;
    }
  }

  static async createAccount(data: CreateAccountRequest): Promise<ChartOfAccount> {
    // Mock implementation
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const newAccount: ChartOfAccount = {
      id: Math.max(...mockChartOfAccounts.map(a => a.id)) + 1,
      code: data.code,
      name: data.name,
      account_type: data.account_type as any,
      normal_balance: data.normal_balance as any,
      property_id: data.property_id,
      parent_id: data.parent_id,
      description: data.description,
      is_active: true,
      is_system_account: false,
      full_code: data.code,
      full_name: data.name,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    mockChartOfAccounts.push(newAccount);
    return newAccount;
  }

  static async updateAccount(accountId: number, data: CreateAccountRequest): Promise<ChartOfAccount> {
    // Mock implementation
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const accountIndex = mockChartOfAccounts.findIndex(a => a.id === accountId);
    if (accountIndex === -1) {
      throw new Error('Account not found');
    }
    
    const updatedAccount = {
      ...mockChartOfAccounts[accountIndex],
      ...data,
      account_type: data.account_type as any,
      normal_balance: data.normal_balance as any,
      updated_at: new Date().toISOString()
    };
    
    mockChartOfAccounts[accountIndex] = updatedAccount;
    return updatedAccount;
  }

  static async deleteAccount(accountId: number): Promise<void> {
    // Mock implementation
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const accountIndex = mockChartOfAccounts.findIndex(a => a.id === accountId);
    if (accountIndex === -1) {
      throw new Error('Account not found');
    }
    
    mockChartOfAccounts.splice(accountIndex, 1);
  }

  static async getTransactions(params: {
    property_id?: number;
    unit_id?: number;
    start_date?: string;
    end_date?: string;
    status?: string;
    page?: number;
    per_page?: number;
  } = {}): Promise<{ data: AccountingTransaction[]; meta: any }> {
    console.log('📡 AccountingService.getTransactions called with params:', params);
    
    // DEBUGGING: Filter out undefined values to see what's actually sent
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([key, value]) => value !== undefined)
    );
    console.log('📡 DEBUG: Clean params being sent to API:', cleanParams);
    console.log('📡 DEBUG: Will this include property filtering?', !!cleanParams.property_id);
    
    const response = await api.get('/accounting/transactions', { params: cleanParams });
    
    // The API returns: { success: true, data: { data: [transactions], total: 8, ... } }
    // We need to extract the transactions array from response.data.data.data
    const transactionsData = response.data.data?.data || [];
    const metaData = response.data.data || {};
    
    console.log('📡 AccountingService - API returned transactions:', transactionsData.length);
    console.log('📡 DEBUG: Sample API response structure:', {
      responseKeys: Object.keys(response.data),
      dataKeys: Object.keys(response.data.data || {}),
      transactionsCount: transactionsData.length,
      firstTransactionKeys: transactionsData[0] ? Object.keys(transactionsData[0]) : 'none'
    });
    
    if (params.unit_id) {
      console.log('📡 Unit filtering applied - unit_id:', params.unit_id);
    }
    
    return {
      data: transactionsData,
      meta: metaData
    };
  }

  static async createTransaction(data: CreateTransactionRequest): Promise<AccountingTransaction> {
    const response = await api.post('/accounting/transactions', data);
    return response.data.data;
  }

  static async updateTransaction(transactionId: number, data: CreateTransactionRequest): Promise<AccountingTransaction> {
    const response = await api.put(`/accounting/transactions/${transactionId}`, data);
    return response.data.data;
  }

  static async deleteTransaction(transactionId: number): Promise<void> {
    await api.delete(`/accounting/transactions/${transactionId}`);
  }

  static async postTransaction(transactionId: number): Promise<AccountingTransaction> {
    const response = await api.post(`/accounting/transactions/${transactionId}/post`);
    return response.data.data;
  }

  static async getAccountBalance(accountId: number, asOfDate?: string): Promise<{
    account: ChartOfAccount;
    balance: number;
    as_of_date: string;
  }> {
    const params = asOfDate ? { as_of_date: asOfDate } : {};
    const response = await api.get(`/accounting/accounts/${accountId}/balance`, { params });
    return response.data.data;
  }

  static async generateReport(data: ReportRequest): Promise<any> {
    const response = await api.post('/accounting/reports', data);
    return response.data.data;
  }

  static async getFinancialOverview(data: {
    property_id?: number;
    unit_id?: number;
    start_date: string;
    end_date: string;
  }): Promise<any> {
    const response = await api.post('/accounting/financial-overview', data);
    return response.data.data;
  }

  // Utility methods
  static formatCurrency(amount: number, currency?: any): string {
    const symbol = currency?.symbol || '$';
    return `${symbol}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  static getAccountTypeColor(accountType: string): string {
    const colors: Record<string, string> = {
      'ASSET': 'bg-blue-100 text-blue-800',
      'LIABILITY': 'bg-red-100 text-red-800',
      'EQUITY': 'bg-purple-100 text-purple-800',
      'REVENUE': 'bg-green-100 text-green-800',
      'EXPENSE': 'bg-yellow-100 text-yellow-800',
      'CONTRA_ASSET': 'bg-blue-100 text-blue-600',
      'CONTRA_LIABILITY': 'bg-red-100 text-red-600',
      'CONTRA_EQUITY': 'bg-purple-100 text-purple-600',
      'CONTRA_REVENUE': 'bg-green-100 text-green-600',
    };
    return colors[accountType] || 'bg-gray-100 text-gray-800';
  }

  static getTransactionStatusColor(status: string): string {
    const colors: Record<string, string> = {
      'DRAFT': 'bg-yellow-100 text-yellow-800',
      'POSTED': 'bg-green-100 text-green-800',
      'REVERSED': 'bg-red-100 text-red-800',
      'CANCELLED': 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  }
}

export default AccountingService;
